import React, { Dispatch, FC, SetStateAction, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import API from 'api/API';
import { SetDeviceResponse, SetTrayResponses } from 'api/Responses/SetResponses';
import Checkbox from 'components/Checkbox/Checkbox';
import DuplicateMergeModal from 'components/ConfirmModal/DuplicateMergeModal';
import InputField from 'components/InputField';
import Label from 'components/Label/Label';
import { useLanguage } from 'context/AppContext';
import { draftActions, SetAction } from 'reducers/DraftReducer/draftActions';
import { PartAction, partActions } from 'reducers/PartReducer/partActions';
import { LoggedInUser, useUser } from 'store/user/useUser';
import { getRegionSpecificAIR } from 'utils/helpers/user.helpers';
import { ModalContentType } from '../SetModal';
import * as Styles from '../SetModal.styles';

interface AddPartProps {
  close: () => void;
  newPart: SetDeviceResponse;
  newPartTray: string | undefined;
  draftTrays: SetTrayResponses;
  dispatchNewPart: Dispatch<PartAction>;
  dispatchDraft: Dispatch<SetAction>;
  setModalContent: Dispatch<SetStateAction<ModalContentType>>;
}

// Workflow to add an existing part to a set
const AddPart: FC<AddPartProps> = ({
  close,
  newPart,
  newPartTray,
  draftTrays,
  dispatchNewPart,
  dispatchDraft,
  setModalContent,
}) => {
  const [language] = useLanguage();
  const user = useUser() as LoggedInUser;
  const [confirmOpen, setConfirmOpen] = useState(false);

  const additionalInspectionText = useMemo(() => {
    return getRegionSpecificAIR(user.region, newPart.part_test_metadata) ?? '';
  }, [newPart.part_test_metadata, user.region]);

  // Adds the part to the selected tray, closes the modal and resets the modal state
  const addPart = (): void => {
    if (newPartTray) {
      try {
        close();
        dispatchDraft(draftActions.partNew(newPart, newPartTray));
        setTimeout(() => setModalContent('selectItem'), 500);
      } catch (error) {
        API.errorHandler(error, language.AddModal.Device.fail);
      }
    }
  };

  // A code already in the tray needs the user to confirm a merge first
  const handleClick = (): void => {
    if (newPartTray && draftTrays[newPartTray]?.parts[newPart.code]) setConfirmOpen(true);
    else addPart();
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Label text={language.TrayTable.reference}>
            <InputField
              variant="outlined"
              fullWidth
              value={newPart.ref}
              onChange={e => dispatchNewPart(partActions.ref(e.target.value))}
              type="number"
            />
          </Label>
        </Grid>
        <Grid item xs={6}>
          <Label text={language.TrayTable.quantity}>
            <InputField
              variant="outlined"
              fullWidth
              value={newPart.qty}
              onChange={e => dispatchNewPart(partActions.qty(e.target.value))}
              type="number"
            />
          </Label>
        </Grid>
        <Grid item xs={12}>
          <Label text={language.DeviceTable.notes}>
            {<InputField variant="outlined" fullWidth value={additionalInspectionText} disabled={true} />}
          </Label>
        </Grid>

        <Grid item xs={3}>
          <Styles.CenteredCheckbox
            control={<Checkbox checked={!!newPart.part_test_metadata?.debris_test} disabled={true} />}
            label={language.DeviceTests.debrisInspection}
          />
        </Grid>

        <Grid item xs={3}>
          <Styles.CenteredCheckbox
            control={<Checkbox checked={!!newPart.part_test_metadata?.functional_test} disabled={true} />}
            label={language.DeviceTests.functionTest}
          />
        </Grid>

        <Grid item xs={3}>
          <Styles.CenteredCheckbox
            control={<Checkbox checked={!!newPart.part_test_metadata?.torque_test} disabled={true} />}
            label={language.DeviceTests.torqueTest}
          />
        </Grid>

        <Grid item xs={3}>
          <Styles.CenteredCheckbox
            control={<Checkbox checked={!!newPart.prone_to_debris} disabled={true} />}
            label={language.DeviceTests.proneToDebris}
          />
        </Grid>
      </Grid>
      <Styles.PartModalButton fullWidth onClick={handleClick}>
        {language.AddModal.Device.add}
      </Styles.PartModalButton>
      <DuplicateMergeModal
        open={confirmOpen}
        close={() => setConfirmOpen(false)}
        confirm={addPart}
        tray={newPartTray ?? ''}
        codes={[newPart.code]}
      />
    </>
  );
};

export default AddPart;
